import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
    BasicModalContent,
} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../utils/resolveErrorMessage";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/system/useMediaQuery";
import { Box, Grid, Typography, Switch, useTheme } from "@mui/joy";
import { Button, FormHelperText } from "@mui/material";
import {
    DeleteRounded,
    NotificationAddRounded,
    NotificationsActiveRounded,
} from "@mui-symbols-material/w300";
import { InstalledBaseIotReportSubscriptions } from "../../graphql/queries/installedBaseIotReportSubscriptions";
import { InfrastructureSelectRootNodeList } from "../../graphql/queries/infrastructureSelectRootNodeList";
import { InstalledBaseNodeOwner } from "../../graphql/queries/installedBaseNodeOwner";
import { nodeBrowserPaginationLimit } from "../../config/paginationLimit";
import { CreateInstalledBaseIotReportSubscription } from "../../graphql/mutations/createInstalledBaseIotReportSubscription";
import { UpdateInstalledBaseIotReportSubscription } from "../../graphql/mutations/updateInstalledBaseIotReportSubscription";
import { DeleteInstalledBaseIotReportSubscription } from "../../graphql/mutations/deleteInstalledBaseIotReportSubscription";
import InfrastructureBrowser from "../InfrastructureBrowser";

interface Props {
    open: boolean;
    onClose: () => void;
    /**
     * Preselected client id. When set, the picker is hidden and the modal
     * pre-fills/edits the report subscription for this client.
     */
    clientId?: string;
    /**
     * Set true to show the client picker (root-only InfrastructureBrowser)
     * inside the modal. Used by the IoT Reports list "Add" flow. Ignored if
     * clientId is provided.
     */
    allowClientPicker?: boolean;
}

interface FormValues {
    inApp: boolean;
    mail: boolean;
    push: boolean;
}

const IotReportSubscriptionModal = ({
    open,
    onClose,
    clientId,
    allowClientPicker,
}: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isFullScreen = useMediaQuery(theme.breakpoints.down("md"));

    const showPicker = !!allowClientPicker && !clientId;

    const [selectedRootNodeId, setSelectedRootNodeId] = useState<string | null>(null);

    const { data, loading } = useQuery(InstalledBaseIotReportSubscriptions, {
        fetchPolicy: "cache-and-network",
    });

    // Only to detect single-company users. Same variables as the embedded
    // picker's first page, so both share one cache entry. Never unpaginated:
    // a global admin sees thousands of companies, and the full list with owner
    // fields fails in the subgraph.
    const { data: rootData } = useQuery(InfrastructureSelectRootNodeList, {
        variables: {
            filter: {},
            pagination: { start: 0, limit: nodeBrowserPaginationLimit },
        },
        fetchPolicy: "cache-and-network",
        skip: !showPicker,
    });

    // With exactly one client there is nothing to choose - preselect it once.
    // One-shot via ref so a deliberate radio deselect is not fought.
    const preselectDoneRef = useRef(false);
    useEffect(() => {
        if (!showPicker || preselectDoneRef.current) return;
        const rootList = rootData?.installedBaseRootNodeList;
        const onlyRoot = rootList?.installedBaseNodes?.[0];
        if (rootList?.total === 1 && onlyRoot?.id) {
            preselectDoneRef.current = true;
            setSelectedRootNodeId(String(onlyRoot.id));
        }
    }, [showPicker, rootData]);

    // The picker only reports the root node id; its client is the node owner.
    const { data: ownerData } = useQuery(InstalledBaseNodeOwner, {
        variables: { id: selectedRootNodeId },
        skip: !showPicker || !selectedRootNodeId,
    });

    const effectiveClientId: string | null = useMemo(() => {
        if (clientId) return clientId;
        if (!selectedRootNodeId) return null;
        const node = ownerData?.installedBaseNode;
        // Ignore a previous selection's result while the new lookup is in flight.
        if (String(node?.id) !== selectedRootNodeId) return null;
        return node?.owner?.id ?? null;
    }, [clientId, selectedRootNodeId, ownerData]);

    const existingSubscription = useMemo(() => {
        if (!effectiveClientId) return null;
        const subs = (data?.installedBaseIotReportSubscriptionList?.subscriptions ?? []) as any[];
        return subs.find(
            (sub: any) => sub?.client?.id === effectiveClientId
        ) ?? null;
    }, [effectiveClientId, data]);

    const mode: "create" | "edit" = existingSubscription ? "edit" : "create";

    const [createSubscriptionMutation] = useMutation(CreateInstalledBaseIotReportSubscription);
    const [updateSubscriptionMutation] = useMutation(UpdateInstalledBaseIotReportSubscription);
    const [deleteSubscriptionMutation, { loading: deleting }] = useMutation(DeleteInstalledBaseIotReportSubscription);

    const handleDelete = async () => {
        if (!existingSubscription?.id) return;
        try {
            const res = await deleteSubscriptionMutation({
                variables: { id: existingSubscription.id },
                refetchQueries: ["InstalledBaseIotReportSubscriptions"],
            });
            if (res.data?.deleteInstalledBaseIotReportSubscription?.success === false) {
                toast.error(
                    res.data.deleteInstalledBaseIotReportSubscription.message ||
                        t("Failed to delete subscription.")
                );
                return;
            }
            toast.success(t("Report subscription removed."));
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        }
    };

    const initialValues: FormValues = {
        inApp: existingSubscription?.inApp ?? true,
        mail: existingSubscription?.mail ?? false,
        push: existingSubscription?.push ?? false,
    };

    const onSubmit = async (
        values: FormValues,
        helpers: FormikHelpers<FormValues>
    ) => {
        try {
            if (!effectiveClientId) {
                toast.error(t("Please pick a company first."));
                helpers.setSubmitting(false);
                return;
            }
            const subscriptionInput = {
                inApp: values.inApp,
                mail: values.mail,
                push: values.push,
            };

            // The IOT_REPORT_SUBSCRIPTION setting container is auto-created
            // (GetOrCreate) by the backend on the first create - no
            // ensureSettingContainer step like in NodeSubscriptionModal.
            if (mode === "edit" && existingSubscription?.id) {
                await updateSubscriptionMutation({
                    variables: {
                        id: existingSubscription.id,
                        input: subscriptionInput,
                    },
                    refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                });
                toast.success(t("Report subscription updated."));
            } else {
                await createSubscriptionMutation({
                    variables: {
                        clientId: effectiveClientId,
                        input: subscriptionInput,
                    },
                    refetchQueries: ["InstalledBaseIotReportSubscriptions"],
                });
                toast.success(t("Report subscription created."));
            }
            onClose();
        } catch (err) {
            toast.error(resolveErrorMessage(err, t));
        } finally {
            helpers.setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullScreen={isFullScreen} fullWidth maxWidth="sm">
            <BasicModalContent
                startIcon={mode === "edit" ? <NotificationsActiveRounded /> : <NotificationAddRounded />}
                onClose={onClose}
                title={
                    <Typography level={"titleSmallLight"}>
                        {mode === "edit"
                            ? t("Edit IoT report subscription")
                            : t("IoT report subscription")}
                    </Typography>
                }
            >
                <Formik
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    enableReinitialize
                >
                    {({ values, setFieldValue, isSubmitting, handleSubmit }) => {
                        const noChannel = !(values.inApp || values.mail || values.push);
                        return (
                            <form onSubmit={handleSubmit}>
                                <Box sx={{ padding: "8px 16px 16px 16px", minWidth: 320 }}>
                                    {showPicker && (
                                        <Box sx={{ mb: 2 }}>
                                            <InfrastructureBrowser
                                                selectRootOnly
                                                label={t("Company")}
                                                value={selectedRootNodeId ?? undefined}
                                                onChange={(id) => setSelectedRootNodeId(id)}
                                            />
                                        </Box>
                                    )}

                                    <Box sx={{ marginBottom: "8px", borderBottom: "1px solid #eee" }}>
                                        <Typography
                                            level="title-sm"
                                            sx={{ paddingBottom: "8px" }}
                                        >
                                            {t("Channels")}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Switch
                                                checked={values.inApp}
                                                onChange={(e) =>
                                                    setFieldValue("inApp", e.target.checked)
                                                }
                                            />
                                            <Box>
                                                <Typography level="body-md">
                                                    {t("Web App")}
                                                </Typography>
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {t("Receive notifications via the web app")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Switch
                                                checked={values.mail}
                                                onChange={(e) =>
                                                    setFieldValue("mail", e.target.checked)
                                                }
                                            />
                                            <Box>
                                                <Typography level="body-md">{t("E-Mail")}</Typography>
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {t("Receive notifications via e-mail")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <Switch
                                                checked={values.push}
                                                onChange={(e) =>
                                                    setFieldValue("push", e.target.checked)
                                                }
                                            />
                                            <Box>
                                                <Typography level="body-md">
                                                    {t("Busch Pfeiffer App")}
                                                </Typography>
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {t("Receive push notifications on your phone")}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ minHeight: 24, mb: 2 }}>
                                        {noChannel && (
                                            <FormHelperText error>
                                                {t("At least one channel must be enabled.")}
                                            </FormHelperText>
                                        )}
                                    </Box>

                                    {/* Equal-width button row like the other modals
                                        (ConfirmDeleteModal, TDAForm): thirds with Delete in
                                        edit mode, halves otherwise. */}
                                    {(() => {
                                        const canDelete = mode === "edit" && !!existingSubscription?.id;
                                        const busy = isSubmitting || deleting;
                                        return (
                                            <Grid container spacing={1} sx={{ pt: 1 }}>
                                                {canDelete && (
                                                    <Grid xs={4}>
                                                        <Button
                                                            fullWidth
                                                            variant="outlined"
                                                            color="error"
                                                            startIcon={<DeleteRounded />}
                                                            onClick={handleDelete}
                                                            disabled={busy}
                                                        >
                                                            {t("Delete")}
                                                        </Button>
                                                    </Grid>
                                                )}
                                                <Grid xs={canDelete ? 4 : 6}>
                                                    <Button fullWidth variant="outlined" onClick={onClose} disabled={busy}>
                                                        {t("Cancel")}
                                                    </Button>
                                                </Grid>
                                                <Grid xs={canDelete ? 4 : 6}>
                                                    <Button
                                                        fullWidth
                                                        type="submit"
                                                        variant="contained"
                                                        disabled={
                                                            busy ||
                                                            loading ||
                                                            noChannel ||
                                                            !effectiveClientId
                                                        }
                                                    >
                                                        {t("Save")}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        );
                                    })()}
                                </Box>
                            </form>
                        );
                    }}
                </Formik>
            </BasicModalContent>
        </Dialog>
    );
};

export default IotReportSubscriptionModal;
